import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useKeycloak } from '@react-keycloak/ssr';
import type { ChannelErrorCounts } from '@/types/channelErrors';
import { asOrganizationId } from '@/session/identity';
import { useRouteOrganization } from '@/session/OrganizationIdentityContext';
import { acquireOperationContext, isSessionTransition, operationRequirement } from '@/session/operation';
import { scopedChannelApi } from '@/session/scopedClients';
import { requestScopeFor } from '@/session/scopedTransport';
import { CACHE_NAMESPACES, CACHE_TTLS, readCache, writeCache } from '@/session/cacheKeys';

export type ErrorCountsContextType = {
  data: ChannelErrorCounts | null;
  loading: boolean;
  error: string | null;
  totalErrors: number;
  pendingAiSuggestions: number;
  refreshErrorCounts: () => Promise<void>;
};

const initialState: ErrorCountsContextType = {
  data: null,
  loading: false,
  error: null,
  totalErrors: 0,
  pendingAiSuggestions: 0,
  refreshErrorCounts: async () => {},
};

export const ErrorCountsContext = createContext<ErrorCountsContextType>(initialState);

const isChannelErrorCounts = (value: unknown): value is ChannelErrorCounts =>
  typeof value === 'object' && value !== null && typeof (value as { total_errors?: unknown }).total_errors === 'number';

type InFlightErrorCounts = {
  key: string;
  controller: AbortController;
  request: Promise<void>;
};

export const ErrorCountsProvider = ({ children }: { children: React.ReactNode }) => {
  const [data, setData] = useState<ChannelErrorCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stateOrganization, setStateOrganization] = useState<string | null>(null);
  const { keycloak, initialized } = useKeycloak();
  const routeOrganization = useRouteOrganization();
  const requestSequenceRef = useRef(0);
  const inFlightRef = useRef<InFlightErrorCounts | null>(null);

  const loadErrorCounts = useCallback(
    async (forceRefresh = false): Promise<void> => {
      if (!initialized || !keycloak?.authenticated || !routeOrganization || !keycloak?.token) return;

      const organization = asOrganizationId(routeOrganization);
      const operation = acquireOperationContext(organization);
      if (!operation.isScopeCurrent()) return;
      const requestKey = `${organization}:${operation.subject ?? ''}:${operation.authGeneration}`;
      const existingRequest = inFlightRef.current;
      if (existingRequest?.key === requestKey) return existingRequest.request;
      if (existingRequest) {
        existingRequest.controller.abort();
        inFlightRef.current = null;
        requestSequenceRef.current += 1;
      }

      setStateOrganization(organization);

      if (!forceRefresh) {
        const cached = readCache<ChannelErrorCounts>({
          scope: 'userOrg',
          namespace: CACHE_NAMESPACES.errorCounts,
          storage: 'localStorage',
          organization,
          subject: operation.subject,
          validate: isChannelErrorCounts,
        });
        if (cached) {
          setData(cached);
          setError(null);
          setLoading(false);
          return;
        }
      }

      const sequence = (requestSequenceRef.current += 1);
      const controller = new AbortController();
      setLoading(true);
      setError(null);
      const request = scopedChannelApi(requestScopeFor(organization))
        .get<ChannelErrorCounts>('astur/integrations/channel/errors/counts', {
          signal: controller.signal,
          operationRequirement: operationRequirement(operation, 'scope'),
        })
        .then((response) => {
          if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent()) return;
          if (!isChannelErrorCounts(response.data)) {
            setError('Failed to load error counts: No data');
            return;
          }
          setData(response.data);
          writeCache({
            scope: 'userOrg',
            namespace: CACHE_NAMESPACES.errorCounts,
            storage: 'localStorage',
            organization,
            subject: operation.subject,
            value: response.data,
            expiresAt: Date.now() + CACHE_TTLS.errorCounts,
          });
        })
        .catch((caught: unknown) => {
          if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent() || isSessionTransition(caught)) return;
          setError('Failed to load error counts');
        })
        .finally(() => {
          if (sequence === requestSequenceRef.current && operation.isScopeCurrent()) setLoading(false);
          if (inFlightRef.current?.request === request) inFlightRef.current = null;
        });

      inFlightRef.current = { key: requestKey, controller, request };
      return request;
    },
    [initialized, keycloak?.authenticated, keycloak?.token, routeOrganization]
  );

  useEffect(() => {
    let active = true;
    const cancelActiveRequest = () => {
      active = false;
      requestSequenceRef.current += 1;
      inFlightRef.current?.controller.abort();
      inFlightRef.current = null;
    };
    if (!routeOrganization) {
      cancelActiveRequest();
      return () => {};
    }
    queueMicrotask(() => {
      if (active) void loadErrorCounts();
    });
    return cancelActiveRequest;
  }, [loadErrorCounts, routeOrganization]);

  const refreshErrorCounts = useCallback(async (): Promise<void> => loadErrorCounts(true), [loadErrorCounts]);

  const stateMatchesRoute = Boolean(routeOrganization && stateOrganization === routeOrganization);
  const currentData = stateMatchesRoute ? data : null;

  const contextValue = useMemo(
    () => ({
      data: currentData,
      loading: stateMatchesRoute ? loading : false,
      error: stateMatchesRoute ? error : null,
      totalErrors: currentData?.total_errors || 0,
      pendingAiSuggestions: currentData?.pending_ai_suggestions || 0,
      refreshErrorCounts,
    }),
    [currentData, loading, error, refreshErrorCounts, stateMatchesRoute]
  );

  return <ErrorCountsContext.Provider value={contextValue}>{children}</ErrorCountsContext.Provider>;
};

export const useErrorCounts = () => useContext(ErrorCountsContext);
